import { useController, type Control, type FieldPath, type FieldValues, type PathValue } from 'react-hook-form'
import { cn } from '@/common/lib/cn/cn'
import './cardSelect.css'

export type CardSelectMenuItem = {
    id: string
    title: string
    description: string
    image?: string | null
}

type BaseProps<T extends FieldValues> = {
    control: Control<T>
    name: FieldPath<T>
    items: CardSelectMenuItem[]
    disabled?: boolean
}

type SingleProps<T extends FieldValues> = BaseProps<T> & {
    mode?: 'single'
}

type MultiProps<T extends FieldValues> = BaseProps<T> & {
    mode: 'multi'
    quantityName: FieldPath<T>
}

export type CardSelectMenuProps<T extends FieldValues> = SingleProps<T> | MultiProps<T>

export const CardSelectMenu = <T extends FieldValues>(props: CardSelectMenuProps<T>) => {
    if (props.mode === 'multi') {
        return <CardSelectMenuMulti {...props} />
    }
    return <CardSelectMenuSingle {...props} />
}

const CardSelectMenuSingle = <T extends FieldValues>({ control, name, items, disabled = false }: SingleProps<T>) => {
    const { field } = useController({ control, name })

    return (
        <div
            className={cn('card-select-menu', { 'card-select-menu-disabled': disabled })}
            role='radiogroup'
            aria-label='Выбор типа изображения'
            aria-disabled={disabled}
        >
            {items.map(item => {
                const checked = field.value === item.id
                return (
                    <label
                        key={item.id}
                        className={cn('card-select-menu-item', { active: checked })}
                        tabIndex={0}
                        role='radio'
                        aria-checked={checked}
                        onKeyDown={e => {
                            if (disabled) return
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                field.onChange(item.id)
                            }
                        }}
                    >
                        <input
                            type='radio'
                            value={item.id}
                            name={name}
                            checked={checked}
                            onChange={() => field.onChange(item.id)}
                            onBlur={field.onBlur}
                            ref={field.ref}
                            className='sr-only'
                            aria-hidden
                            disabled={disabled}
                        />
                        {item.image && <img src={item.image} alt={item.title} className='rounded-md' />}
                        <div className='flex justify-center items-center'>
                            <div className='card-select-menu-item-content'>
                                <h4>{item.title}</h4>
                                <p>{item.description}</p>
                            </div>
                        </div>
                    </label>
                )
            })}
        </div>
    )
}

const CardSelectMenuMulti = <T extends FieldValues>({
    control,
    name,
    quantityName,
    items,
    disabled = false,
}: MultiProps<T>) => {
    const { field: idsField } = useController({ control, name })
    const { field: qtyField } = useController({ control, name: quantityName })

    const selectedIds: string[] = Array.isArray(idsField.value) ? (idsField.value as string[]) : []
    const quantities: number[] = Array.isArray(qtyField.value) ? (qtyField.value as number[]) : []

    const setIdsAndQuantities = (nextIds: string[], nextQuantities: number[]) => {
        idsField.onChange(nextIds as PathValue<T, FieldPath<T>>)
        qtyField.onChange(nextQuantities as PathValue<T, FieldPath<T>>)
    }

    const handleToggle = (itemId: string) => {
        if (disabled) return
        const index = selectedIds.indexOf(itemId)
        if (index >= 0) {
            setIdsAndQuantities(
                selectedIds.filter((_, i) => i !== index),
                quantities.filter((_, i) => i !== index)
            )
            return
        }
        setIdsAndQuantities([...selectedIds, itemId], [...quantities, 1])
    }

    return (
        <div
            className={cn('card-select-menu', { 'card-select-menu-disabled': disabled })}
            role='group'
            aria-label='Выбор вариантов генерации'
            aria-disabled={disabled}
        >
            {items.map(item => {
                const index = selectedIds.indexOf(item.id)
                const checked = index >= 0

                return (
                    <div
                        key={item.id}
                        className={cn('card-select-menu-item items-center justify-between gap-3', { active: checked })}
                    >
                        <label className='flex min-w-0 flex-1 cursor-pointer items-center gap-4'>
                            <input
                                type='checkbox'
                                className='h-4 w-4 shrink-0 rounded border-[var(--pixorion-gray-2)]'
                                checked={checked}
                                disabled={disabled}
                                onChange={() => handleToggle(item.id)}
                                aria-label={`Включить вариант: ${item.title}`}
                            />
                            {item.image && <img src={item.image} alt={item.title} className='rounded-md' />}
                            <div className='flex min-w-0 flex-1 justify-start'>
                                <div className='card-select-menu-item-content'>
                                    <h4>{item.title}</h4>
                                    <p>{item.description}</p>
                                </div>
                            </div>
                        </label>
                    </div>
                )
            })}
        </div>
    )
}
